export const USER_ROLES = {
    ADMIN: "Admin",
    SUPER_ADMIN: "SuperAdmin",
    RN: "RN",
    PT: "PT",
    PTA: "PTA",
    OT: "OT",
    OTA: "OTA",
    ST: "ST",
    MSW: "MSW",
    CG: "CG",
    CNA: "CNA",
    HHA: "HHA",
    LVN: "LVN",
    OA: "OA",
    SRVY: "SRVY",
    CON: 'CON'
};

/** Roles that may call `/attendance/current-timing` (prefetch + cache). */
export const ATTENDANCE_TIMING_PREFETCH_ROLES = [USER_ROLES.ADMIN, USER_ROLES.OA, USER_ROLES.CG, USER_ROLES.CNA];

/** Map display names / aliases to canonical role codes (matches users.role.name from API). */
export const ROLE_CODE_ALIASES = {
    Caregiver: "CG",
    "Certified Nursing Assistant": "CNA",
    "Office Assistant": "OA",
    Admin: "ADMIN",
    Administrator: "ADMIN",
    Surveyor: "SRVY",
};

/** Document tabs hidden per role code — extend here when a role should not see a tab. */
export const DOCUMENT_TABS_HIDDEN_BY_ROLE = {
    CG: ["evaluation-forms", "service-log"],
    CNA: ["evaluation-forms", "service-log"],
};

/** Roles excluded from the admin Evaluation module (role picker + history table). */
export const EVALUATION_MODULE_EXCLUDED_ROLES = ["CG", "CNA"];

export const ALL_DOCUMENT_TABS = [
    "application-forms",
    "medical-forms",
    "tax-forms",
    "evaluation-forms",
    "credentials",
    "service-log",
];

export const normalizeRoleCode = (roleName) => {
    if (!roleName) return "";
    const key = String(roleName).trim();
    if (ROLE_CODE_ALIASES[key]) return ROLE_CODE_ALIASES[key];
    return key.toUpperCase();
};

export const resolveUserRoleName = (user) => {
    if (!user) return "";
    if (user.role?.name) return user.role.name;
    if (typeof user.role === "string") return user.role;
    if (user.roleName) return user.roleName;
    // useCurrentUser() strips nested role — fall back to roleId map
    if (user.roleId != null && ROLE_IDS[user.roleId]) {
        return ROLE_IDS[user.roleId];
    }
    return "";
};

export const getDocumentTabsForRole = (roleName) => {
    const code = normalizeRoleCode(roleName);
    const hidden = DOCUMENT_TABS_HIDDEN_BY_ROLE[code] || [];
    return ALL_DOCUMENT_TABS.filter((tab) => !hidden.includes(tab));
};

export const isDocumentTabVisibleForRole = (tab, roleName) =>
    getDocumentTabsForRole(roleName).includes(tab);

export const isRoleExcludedFromEvaluationModule = (roleName) =>
    EVALUATION_MODULE_EXCLUDED_ROLES.includes(normalizeRoleCode(roleName));

/**
 * Invoice + General Forms — all tenant employees except Surveyor.
 * Single source for ROUTE_PERMISSIONS (sidebar + RoleBasedRoute).
 *
 * Keep DB `role_module_permissions` in sync via backend migration
 * `20260825120000_cg_cna_role_module_permissions` and
 * `src/internal/user/constants/role-module-permissions.ts`.
 */
export const ALL_EMPLOYEE_MODULE_ROLES = [
    USER_ROLES.ADMIN,
    USER_ROLES.OA,
    USER_ROLES.RN,
    USER_ROLES.LVN,
    USER_ROLES.PT,
    USER_ROLES.PTA,
    USER_ROLES.OT,
    USER_ROLES.OTA,
    USER_ROLES.ST,
    USER_ROLES.HHA,
    USER_ROLES.MSW,
    USER_ROLES.CG,
    USER_ROLES.CNA,
    USER_ROLES.CON,
];

export const ROUTE_PERMISSIONS = {
    "/": [USER_ROLES.ADMIN, USER_ROLES.OA, USER_ROLES.CG, USER_ROLES.CNA],
    "/employees": [USER_ROLES.ADMIN],
    "/applications": [USER_ROLES.ADMIN, USER_ROLES.RN, USER_ROLES.PT, USER_ROLES.PTA, USER_ROLES.OT, USER_ROLES.OTA, USER_ROLES.LVN, USER_ROLES.MSW, USER_ROLES.CG, USER_ROLES.CNA, USER_ROLES.ST, USER_ROLES.HHA, USER_ROLES.CON, USER_ROLES.OA, USER_ROLES.SRVY],
    "/patients": [USER_ROLES.ADMIN, USER_ROLES.OA, USER_ROLES.RN, USER_ROLES.LVN, USER_ROLES.CG, USER_ROLES.CNA, USER_ROLES.PT, USER_ROLES.PTA, USER_ROLES.OT, USER_ROLES.OTA, USER_ROLES.ST, USER_ROLES.HHA, USER_ROLES.MSW, USER_ROLES.CON, USER_ROLES.SRVY],
    "/payroll": [USER_ROLES.ADMIN],
    "/evaluation": [USER_ROLES.ADMIN, USER_ROLES.SRVY],
    "/documents": [USER_ROLES.ADMIN, USER_ROLES.RN, USER_ROLES.PT, USER_ROLES.PTA, USER_ROLES.OT, USER_ROLES.OTA, USER_ROLES.LVN, USER_ROLES.MSW, USER_ROLES.CG, USER_ROLES.CNA, USER_ROLES.ST, USER_ROLES.HHA, USER_ROLES.CON, USER_ROLES.OA, USER_ROLES.SRVY],
    "/invoice": ALL_EMPLOYEE_MODULE_ROLES,
    "/bills": [USER_ROLES.ADMIN],
    "/platform-billing": [USER_ROLES.SUPER_ADMIN],
    "/reports": [USER_ROLES.ADMIN],
    "/general-forms": ALL_EMPLOYEE_MODULE_ROLES,
    "/surveyor": [USER_ROLES.ADMIN, USER_ROLES.OA],
    "/coverage-area": [USER_ROLES.ADMIN, USER_ROLES.OA],
    // Office-only modules. Add a role here to widen access — this one entry
    // drives both the sidebar item and the route guard.
    "/qa-center": [USER_ROLES.ADMIN, USER_ROLES.OA],
    "/billing": [USER_ROLES.ADMIN, USER_ROLES.OA],
    "/libraries": [USER_ROLES.ADMIN, USER_ROLES.OA],
    "/training": [
        USER_ROLES.ADMIN,
        USER_ROLES.RN,
        USER_ROLES.PT,
        USER_ROLES.PTA,
        USER_ROLES.OT,
        USER_ROLES.OTA,
        USER_ROLES.ST,
        USER_ROLES.MSW,
        USER_ROLES.CG,
        USER_ROLES.CNA,
        USER_ROLES.HHA,
        USER_ROLES.LVN,
        USER_ROLES.OA,
        USER_ROLES.SRVY,
        USER_ROLES.CON,
    ],
    "/support": [
        USER_ROLES.ADMIN,
        USER_ROLES.RN,
        USER_ROLES.PT,
        USER_ROLES.PTA,
        USER_ROLES.OT,
        USER_ROLES.OTA,
        USER_ROLES.ST,
        USER_ROLES.MSW,
        USER_ROLES.CG,
        USER_ROLES.CNA,
        USER_ROLES.HHA,
        USER_ROLES.LVN,
        USER_ROLES.OA,
        USER_ROLES.SRVY,
        USER_ROLES.CON,
    ],
};

export const ROLE_IDS = {
    // Display hints only — always resolve role ids from GET /roles at runtime.
    1: "ADMIN",
    2: "RN",
    3: "PT",
    4: "PTA",
    5: "OT",
    6: "OTA",
    7: "ST",
    8: "MSW",
    9: "HHA",
    10: "LVN",
    11: "OA",
    12: "SRVY",
    13: "CON",
    14: "CG",
    15: "CNA",
};

export const ROLE_DETAILS = {
    // Display metadata only — ids may not match DB (e.g. SuperAdmin). Use GET /roles for ids.
    1: { id: 1, code: "ADMIN", name: "Administrator", description: "Administrator with full access to all modules", status: "Active" },
    2: { id: 2, code: "RN", name: "Registered Nurse", description: "Registered Nurse", status: "Active" },
    3: { id: 3, code: "PT", name: "Physical Therapist", description: "Physical Therapist", status: "Active" },
    4: { id: 4, code: "PTA", name: "Physical Therapist Assistant", description: "Physical Therapist Assistant", status: "Active" },
    5: { id: 5, code: "OT", name: "Occupational Therapist", description: "Occupational Therapist", status: "Active" },
    6: { id: 6, code: "OTA", name: "Occupational Therapist Assistant", description: "Occupational Therapist Assistant", status: "Active" },
    7: { id: 7, code: "ST", name: "Speech Therapist", description: "Speech Therapist", status: "Active" },
    8: { id: 8, code: "MSW", name: "Medical Social Worker", description: "Medical Social Worker", status: "Active" },
    9: { id: 9, code: "HHA", name: "Home Health Aide", description: "Home Health Aide", status: "Active" },
    10: { id: 10, code: "LVN", name: "Licensed Vocational Nurse", description: "Licensed Vocational Nurse", status: "Active" },
    11: { id: 11, code: "OA", name: "Office Assistant", description: "Office Assistant", status: "Active" },
    12: { id: 12, code: "SRVY", name: "Surveyor", description: "Surveyor", status: "Active" },
    13: { id: 13, code: "CON", name: "Contractor", description: "Contractor", status: "Active" },
    14: { id: 14, code: "CG", name: "Caregiver", description: "Caregiver", status: "Active" },
    15: { id: 15, code: "CNA", name: "Certified Nursing Assistant", description: "Certified Nursing Assistant", status: "Active" },
};
